import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * AuthNavigator handles automatic navigation based on auth state
 * Placed inside App.tsx after AuthProvider but inside Router
 */
export const AuthNavigator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, loading, isSMSVerificationPending } = useAuth();

  useEffect(() => {
    if (loading) return;

    if (isSMSVerificationPending) {
      console.log('SMS verification pending, skipping auto-redirect');
      return;
    }

    if (!user) return;

    // Don't redirect if user is in account setup flow
    const inSetupFlow =
      sessionStorage.getItem('allow_unverified_setup') === '1';
    if (inSetupFlow) {
      console.log('User in setup flow, skipping auto-redirect');
      return;
    }

    // Don't redirect if on SMS 2FA verification page
    if (location.pathname === '/verify-sms-2fa') {
      console.log('On SMS 2FA page, skipping auto-redirect');
      return;
    }

    // Don't redirect if on SMS 2FA setup page
    if (location.pathname === '/setup-sms-2fa') {
      console.log('On SMS 2FA setup page, skipping auto-redirect');
      return;
    }

    // Don't redirect if already on correct dashboard
    if (
      (isAdmin && location.pathname.startsWith('/admin')) ||
      (!isAdmin && location.pathname.startsWith('/dashboard'))
    ) {
      return;
    }

    // Don't redirect if on public pages
    const publicPaths = [
      '/',
      '/pricing',
      '/login',
      '/create-account',
      '/checkout',
    ];
    if (publicPaths.includes(location.pathname)) {
      // User is authenticated but on a public page - redirect to dashboard
      if (isAdmin) {
        console.log(
          'Admin user on public page, redirecting to admin dashboard'
        );
        navigate('/admin/dashboard', { replace: true });
      } else {
        console.log('User on public page, redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [
    user,
    isAdmin,
    loading,
    isSMSVerificationPending,
    location.pathname,
    navigate,
  ]);

  return null; // This component only handles navigation, renders nothing
};
